/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useEventPhase.ts
 *
 * Rôle :
 *   Hook React qui expose la **phase courante** de l'événement TNE 2026
 *   ('before' | 'during' | 'after') et un compte à rebours / compte à
 *   rebrousse, en lisant en priorité `tne_site_settings` côté serveur,
 *   et en retombant sur les constantes `DEFAULT_EVENT_*` si le backend
 *   n'a pas encore livré l'endpoint OU si la requête échoue.
 *
 *   Trois sources de vérité, par ordre de priorité décroissant :
 *     1. **Override URL** `?devPhase=before|during|after` (démo DRANE).
 *     2. **Override localStorage** `tne.dev.eventPhaseOverride` (démo + dev).
 *     3. **Backend** `/api/me/site-settings.php?keys=event.date_iso,event.end_iso,event.timezone`
 *        (cache TanStack Query 60 s).
 *     4. **Fallback hard-codé** (`DEFAULT_EVENT_*` dans `utils/constants.ts`).
 *
 *   Le hook **re-render automatiquement** quand la phase change : un
 *   `setInterval` interne (30 s) déclenche un setState pour pousser le
 *   composant à recalculer. Idéal pour passer de 'before' → 'during'
 *   pile à 9 h le 27 mai 2026 sans recharger la page.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE, n'importe quel salon DRANE peut adopter
 *   le pattern. Pour un autre événement : ajuster les constantes
 *   `DEFAULT_EVENT_*` dans `utils/constants.ts`. Le hook peut aussi
 *   servir tel quel pour des sous-événements (ex. ouverture VIP,
 *   keynote 14h-15h) en passant des dates custom (à venir : signature
 *   `useEventPhase({ start, end })`).
 *
 * Voir : CLAUDE.md §F12, §14.0 ; V1 SQL `tne_settings.event_*` ; V11 SQL
 *        `tne_site_settings.event.*` ; `utils/eventPhase.ts`.
 *
 * @author Mika
 * @since  2026-05-06
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/api/client';
import {
  DEFAULT_EVENT_START,
  DEFAULT_EVENT_END,
  DEFAULT_EVENT_TZ,
  EVENT_PHASE_OVERRIDE_STORAGE_KEY,
  EVENT_PHASE_OVERRIDE_URL_PARAM,
  ADMIN_PHASE_REVEAL_STORAGE_KEY,
} from '@/utils/constants';
import {
  eventPhase,
  hoursUntil,
  minutesUntil,
  daysSince,
  estJourJ as calculerJourJ,
  joursCivilsAvant,
  type EventPhase,
} from '@/utils/eventPhase';

/** Forme typée d'une réponse de `/api/me/site-settings.php` (filtrée à event.*). */
interface SiteSettingsEventResponse {
  ok: boolean;
  settings: {
    'event.date_iso'?: string;
    'event.end_iso'?: string;
    'event.timezone'?: string;
    // FIX 2026-05-26 : Override SERVER global (V49). Valeurs autorisées :
    // '' | 'before' | 'during' | 'after'. Vide = calcul par date.
    // Permet au super_admin de forcer la phase pour TOUS les utilisateurs
    // depuis /admin/settings (et non plus seulement pour son cookie/URL).
    // Cf. CLAUDE.md §5 entrée 2026-05-26 « phase override global ».
    'event.phase_override'?: string;
  };
}

/** Endpoint relatif (cohérent §F2 ETag + cache 60 s).
 *  FIX 2026-05-26 : Ajout `event.phase_override` (V49) pour cascade override
 *  niveau 3 (server-wide). me/site-settings.php filtre déjà is_public=1.
 */
const SITE_SETTINGS_ENDPOINT =
  'me/site-settings.php?keys=event.date_iso,event.end_iso,event.timezone,event.phase_override';

/** Clé TanStack Query partagée (un seul fetch global pour tous les composants). */
export const EVENT_PHASE_QUERY_KEY = ['public', 'site-settings', 'event'] as const;

/** Intervalle de re-render interne (30 s), déclenche le recalcul de phase. */
const RE_RENDER_INTERVAL_MS = 30 * 1_000;

/**
 * Résultat exposé par `useEventPhase`.
 *
 * - `phase` : phase courante au moment du rendu (recalculée toutes
 *   les 30 s grâce au polling interne).
 * - `dateStart`, `dateEnd` : Date résolues (priorité override → backend → fallback).
 * - `daysToEvent` / `hoursToEvent` / `minutesToEvent` : compte à rebours
 *   vers `dateStart`. **0 si `phase !== 'before'`** (évite l'affichage
 *   « J-0 » bizarre quand on est en plein dedans).
 * - `isLive` : alias `phase === 'during'`.
 * - `isPast`  : alias `phase === 'after'`.
 * - `daysSinceEvent` : jours écoulés depuis `dateEnd`. **0 si phase n'est
 *   pas 'after'**.
 */
export interface UseEventPhaseResult {
  phase: EventPhase;
  dateStart: Date;
  dateEnd: Date;
  daysToEvent: number;
  hoursToEvent: number;
  minutesToEvent: number;
  isLive: boolean;
  isPast: boolean;
  daysSinceEvent: number;
  /**
   * NOLAS 2026 (2026-06-23), `true` si le mode « Aperçu intégral admin »
   * est actif dans ce navigateur (cf. ADMIN_PHASE_REVEAL_STORAGE_KEY). Dans
   * ce cas, `phase` a été forcée à `during` pour déverrouiller tous les
   * écrans. Sert aux composants d'affichage (ex. PhaseBadge) pour signaler
   * discrètement que la phase montrée est un aperçu, pas la réalité.
   */
  revealAll: boolean;
  /**
   * 2026-10-07 (Mika : « le jour J, "compte à rebours" ne doit pas être
   * présent ») : vrai le jour civil du début de l'événement, À L'HEURE DU
   * SALON (`event.timezone`), dès minuit, même si la phase vaut encore
   * 'before' avant l'ouverture. Faux sous un override de démonstration.
   * Facultatif pour les doubles de test existants : absent vaut faux.
   * Mikael - chef de projet - 2026-10-07
   */
  estJourJ?: boolean;
}

/**
 * Lit l'override URL `?devPhase=...` si présent et valide.
 *
 * @returns La phase forcée, ou `null` si absente/invalide/SSR.
 */
function readUrlOverride(): EventPhase | null {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(EVENT_PHASE_OVERRIDE_URL_PARAM);
    if (raw === 'before' || raw === 'during' || raw === 'after') {
      return raw;
    }
    return null;
  } catch {
    // Edge case : URL malformée → on ignore l'override.
    return null;
  }
}

/**
 * Lit l'override localStorage si présent et valide.
 *
 * @returns La phase forcée, ou `null` si absente/invalide/SSR/private mode.
 */
function readStorageOverride(): EventPhase | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(EVENT_PHASE_OVERRIDE_STORAGE_KEY);
    if (raw === 'before' || raw === 'during' || raw === 'after') {
      return raw;
    }
    return null;
  } catch {
    // Edge case : localStorage indispo (private mode Safari) → on ignore.
    return null;
  }
}

/**
 * Lit le flag « Aperçu intégral admin » dans localStorage.
 *
 * @returns `true` si le mode est actif (déverrouille tout en forçant `during`).
 *          `false` si absent / SSR / private mode.
 */
function readPhaseReveal(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ADMIN_PHASE_REVEAL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Lit l'override SERVER global depuis `tne_site_settings.event.phase_override`
 * (V49, is_public=1, lu via /api/me/site-settings.php).
 *
 * Différence avec les overrides URL/localStorage :
 *   - URL/localStorage : portée = UN navigateur (le super_admin lui-même).
 *   - SERVER           : portée = TOUS les utilisateurs (anonymes inclus).
 *
 * Cas d'usage Mika 2026-05-26 : « rendre l'application en mode jour J
 * pour tous » depuis /admin/settings, vérifier si un bug est lié au cache
 * navigateur, faire une démo coordonnée, ou rattraper une dérive d'horloge
 * serveur le jour J.
 *
 * @param data  Réponse TanStack Query de `/api/me/site-settings.php`.
 *              Peut être `null` (loading, erreur, fallback) → renvoie null.
 * @returns     La phase forcée, ou `null` si vide / invalide / absente.
 */
function readServerOverride(
  data: SiteSettingsEventResponse | null | undefined,
): EventPhase | null {
  const raw = data?.settings?.['event.phase_override'];
  if (raw === 'before' || raw === 'during' || raw === 'after') {
    return raw;
  }
  return null;
}

/**
 * Construit des dates fictives proches de `now()` pour simuler une phase
 * forcée par override. Permet aux composants downstream qui calculent
 * leur propre compte à rebours de fonctionner même en mode démo.
 *
 * @param phase  Phase forcée par l'override.
 * @param now    Instant courant.
 * @returns      Paire `{ start, end }` cohérente avec la phase demandée.
 */
function fakeDatesForOverride(
  phase: EventPhase,
  now: Date,
): { start: Date; end: Date } {
  const ms = now.getTime();
  if (phase === 'before') {
    // Événement dans 12 jours, durée 8 h.
    const start = new Date(ms + 12 * 24 * 60 * 60 * 1_000);
    const end = new Date(start.getTime() + 8 * 60 * 60 * 1_000);
    return { start, end };
  }
  if (phase === 'during') {
    // Événement actuellement en cours : commencé il y a 1 h, finit dans 7 h.
    const start = new Date(ms - 60 * 60 * 1_000);
    const end = new Date(ms + 7 * 60 * 60 * 1_000);
    return { start, end };
  }
  // phase === 'after'
  // Événement terminé il y a 3 jours, durée 8 h.
  const end = new Date(ms - 3 * 24 * 60 * 60 * 1_000);
  const start = new Date(end.getTime() - 8 * 60 * 60 * 1_000);
  return { start, end };
}

/**
 * Charge les dates événement depuis `tne_site_settings` (cache 60 s).
 *
 * Comportement fail-safe : si l'API renvoie une erreur (4xx, 5xx, réseau),
 * on retombe sur les constantes : TanStack Query évite le retry infini.
 *
 * @internal Exposé séparément pour permettre à d'autres composants de
 *           lire les mêmes settings sans déclencher un nouveau fetch.
 */
export function useEventDatesQuery() {
  return useQuery<SiteSettingsEventResponse | null, Error>({
    queryKey: EVENT_PHASE_QUERY_KEY,
    queryFn: async () => {
      try {
        const data = await apiRequest<SiteSettingsEventResponse>(
          SITE_SETTINGS_ENDPOINT,
        );
        return data;
      } catch (err) {
        // ApiError 404 (endpoint pas encore livré) ou erreur réseau
        // on renvoie null pour basculer sur le fallback. Pas de throw,
        // pas de retry agressif (cf. fail-safe §10.10).
        if (err instanceof ApiError) return null;
        // Erreurs autres (TypeError fetch), pareil, fallback gracieux.
        return null;
      }
    },
    staleTime: 60_000, // 60 s, cohérent avec Cache-Control HTTP côté serveur
    gcTime: 5 * 60_000,
    retry: false, // Pas de retry : si l'endpoint est down, on utilise le fallback
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook principal, retourne la phase de l'événement TNE 2026 + compte
 * à rebours.
 *
 * @returns `UseEventPhaseResult` (phase, dateStart, dateEnd, compteurs,
 *          aliases isLive/isPast).
 *
 * @example
 *   function MaPage() {
 *     const { phase, daysToEvent, isLive } = useEventPhase();
 *     if (phase === 'before') return <CountdownPage days={daysToEvent} />;
 *     if (isLive)            return <LivePage />;
 *     return <RetrospectivePage />;
 *   }
 */
export function useEventPhase(): UseEventPhaseResult {
  // Tick interne, incrémenté toutes les 30 s pour forcer un re-render
  // (et donc recalculer la phase). On stocke un nombre, pas un Date,
  // pour éviter les inégalités de référence Date.
  const [, setTick] = useState<number>(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTick((prev) => prev + 1);
    }, RE_RENDER_INTERVAL_MS);
    return () => {
      window.clearInterval(interval);
    };
  }, []);

  // Backend (avec fallback gracieux dans la queryFn).
  const { data } = useEventDatesQuery();

  // FIX 2026-05-26 : Cascade override à 3 niveaux (priorité décroissante) :
  //   1. URL  `?devPhase=...`, démos DRANE, démonstrateurs (UN onglet).
  //   2. localStorage, dev + démo locale (UN navigateur).
  //   3. SERVER (V49), `event.phase_override` BDD, portée GLOBALE
  //      (TOUS les utilisateurs, anonymes inclus). Édité par super_admin
  //      via /admin/settings.
  // Cf. CLAUDE.md §5 entrée 2026-05-26 « phase override global ».
  const urlOverride = readUrlOverride();
  const storageOverride = readStorageOverride();
  const serverOverride = readServerOverride(data ?? null);
  // NOLAS 2026 (2026-06-23), « Aperçu intégral admin » : dernier maillon de
  // la cascade. Un override EXPLICITE (URL / sélecteur démo / serveur) reste
  // prioritaire ; à défaut, le reveal force `during` pour tout déverrouiller.
  const revealAll = readPhaseReveal();
  const override: EventPhase | null =
    urlOverride ?? storageOverride ?? serverOverride ?? (revealAll ? 'during' : null);

  const now = new Date();

  // Si override actif → on génère des dates fictives proches de `now`
  // (cohérentes avec la phase demandée).
  if (override !== null) {
    const { start, end } = fakeDatesForOverride(override, now);
    return computeResult(now, start, end, override, revealAll, DEFAULT_EVENT_TZ);
  }

  // Pas d'override : on lit le backend, sinon les fallbacks.
  const startIso =
    data?.settings?.['event.date_iso'] ?? DEFAULT_EVENT_START;
  const endIso =
    data?.settings?.['event.end_iso'] ?? DEFAULT_EVENT_END;
  // Note : le timezone est lu pour traçabilité mais n'affecte pas le
  // calcul (ISO 8601 + offset suffit). Conservé pour usage futur (ex.
  // affichage d'un suffixe « (heure de Paris) » dans l'UI).
  // 2026-10-07 : le fuseau sert désormais au jour civil (« J-2 », jour J).
  const fuseau = data?.settings?.['event.timezone'] ?? DEFAULT_EVENT_TZ;

  const dateStart = new Date(startIso);
  const dateEnd = new Date(endIso);

  return computeResult(now, dateStart, dateEnd, undefined, revealAll, fuseau);
}

/**
 * Construit le résultat final à partir des dates résolues. Centralise
 * le calcul pour éviter la duplication entre le chemin override et le
 * chemin normal.
 *
 * @param now           Instant courant.
 * @param dateStart     Début de l'événement.
 * @param dateEnd       Fin de l'événement.
 * @param forcedPhase   Phase forcée (cas override), si fournie, on
 *                      l'utilise tel quel ; sinon on la calcule.
 */
function computeResult(
  now: Date,
  dateStart: Date,
  dateEnd: Date,
  forcedPhase?: EventPhase,
  revealAll: boolean = false,
  fuseau: string = DEFAULT_EVENT_TZ,
): UseEventPhaseResult {
  const phase: EventPhase = forcedPhase ?? eventPhase(now, dateStart, dateEnd);

  // Compteurs : nuls si on n'est pas dans la phase pertinente.
  // FIX 2026-05-26, `daysToEvent` utilise désormais `daysUntilCalendar`
  // (jours civils calendaires) au lieu de `daysUntil` (heures cumulées
  // / 24, qui donnait « J-0 » 14h avant l'événement). La convention UX
  // française « J-N » = N jours civils avant le jour J. Cf. CLAUDE.md §5.
  // 2026-10-07 : jours civils À L'HEURE DU SALON (un téléphone réglé sur
  // un autre fuseau ne décale plus le « J-N »). Mikael - chef de projet - 2026-10-07
  const daysToEvent = phase === 'before' ? joursCivilsAvant(now, dateStart, fuseau) : 0;
  const hoursToEvent = phase === 'before' ? hoursUntil(now, dateStart) : 0;
  const minutesToEvent = phase === 'before' ? minutesUntil(now, dateStart) : 0;
  const daysSinceEvent = phase === 'after' ? daysSince(now, dateEnd) : 0;

  return {
    phase,
    dateStart,
    dateEnd,
    daysToEvent,
    hoursToEvent,
    minutesToEvent,
    isLive: phase === 'during',
    isPast: phase === 'after',
    daysSinceEvent,
    revealAll,
    estJourJ: forcedPhase === undefined && calculerJourJ(now, dateStart, fuseau),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//   Ce hook combine plusieurs patterns réutilisables :
//     - TanStack Query avec fallback gracieux (queryFn renvoie null
//       au lieu de throw), pattern qu'on peut copier dans NOLAS.
//     - Polling 30 s via setInterval + setState (recalcule la phase).
//     - Override URL + localStorage pour démo / dev (utile en NOLAS
//       aussi pour tester les écrans rétrospective).
//   Pour NOLAS : copier `eventPhase.ts` (utilitaire pur), recoder le
//   hook en JS vanilla (pas de TanStack Query côté NOLAS), garder la
//   même API.
// Idées d'évolution :
//   - Signature `useEventPhase({ start, end })` pour des sous-événements
//     (keynote 14h-15h, vernissage VIP 17h-19h, etc.).
//   - Variante `useEventPhaseWithSubevents(events: Subevent[])` pour
//     piloter un agenda complet.
// ─────────────────────────────────────────────────────────────────────────
